import type { AdditionalCommitmentDTO, LicenseDTO, LicenseUpdateDTO } from '../license.types';
import type { ProductPlanOption } from '@repositories/softwareRepository/software.types';
import dayjs from 'dayjs';
import { ProductPlanModel } from '@repositories/softwareRepository/models/ProductPlanModel';
import { SubscriptionSimpleModel } from '@models/SubscriptionSimpleModel';
import { SubscriptionUserModel } from '@models/SubscriptionUserModel';

export class LicenseModel {
  private readonly _dto: LicenseDTO;
  private readonly _additionalCommitments: AdditionalCommitmentDTO[];
  private readonly _plan: ProductPlanModel;
  private readonly _subscription: SubscriptionSimpleModel;
  private readonly _subscriptionUsers: SubscriptionUserModel[];
  private readonly _planOption: ProductPlanOption;

  constructor(dto: LicenseDTO) {
    this._dto = dto;

    this._additionalCommitments = dto.additionalCommitments ?? [];
    this._plan = new ProductPlanModel(dto.plan);
    this._subscription = new SubscriptionSimpleModel(dto.subscription);
    this._subscriptionUsers =
      dto.subscriptionUsers?.map(userDto => new SubscriptionUserModel(userDto, this._subscription.id)) ?? [];

    this._planOption = dto.planOption ?? {
      optionName: '',
      paymentOption: 'PREPAID',
      paymentPeriod: 'ONCE',
      billingCycle: 'MONTH',
      billingPeriodType: 'CALENDAR_MONTH',
      planOptionId: '',
      productPlanId: '',
      usageMetricPrices: [],
      unitPeriod: 'MONTH',
    };
  }

  get originDTO(): LicenseDTO {
    return this._dto;
  }

  get additionalCommitments() {
    return this._additionalCommitments;
  }

  get additionalProperties() {
    return this._dto.additionalProperties ?? {};
  }

  get contractUnitPrice() {
    return this._dto.contractUnitPrice;
  }

  get currencyUnit() {
    return this._dto.currencyUnit;
  }

  get endDate() {
    return this._dto.endDate;
  }

  get licenseId() {
    return this._dto.licenseId;
  }

  get licenseStatus() {
    return this._dto.licenseStatus;
  }

  get licenseUserType() {
    return this._dto.licenseUserType;
  }

  get plan() {
    return this._plan;
  }

  get planOption() {
    return this._planOption;
  }

  get createdDate() {
    return this._dto.createdDate ?? '';
  }

  get startDate() {
    return this._dto.startDate;
  }

  get statusMessage() {
    return this._dto.statusMessage;
  }

  get subscription() {
    return this._subscription;
  }

  get subscriptionUsers() {
    return this._subscriptionUsers;
  }

  get subscriptionUserCount() {
    return this._dto.subscriptionUserCount ?? 0;
  }

  get timeZone() {
    return this._dto.timeZone;
  }

  get usageCommitmentBased() {
    return this._dto.usageCommitmentBased;
  }

  get licenseCode() {
    return this._dto.licenseCode;
  }

  get renewalDate() {
    return this._dto.renewalDate;
  }

  get isActive() {
    const status = this._dto.licenseStatus;
    if (status === 'EXPIRED') {
      return false;
    }
    const today = dayjs();
    const startDate = this.startDate ? dayjs(this.startDate) : null;
    const endDate = this.endDate ? dayjs(this.endDate) : null;
    let isActiveCheck = false;

    if (startDate === null && endDate !== null) {
      isActiveCheck = today.isBefore(endDate);
    } else if (startDate !== null) {
      if (endDate) {
        isActiveCheck = today.isAfter(startDate) && today.isBefore(endDate);
      } else {
        isActiveCheck = today.isAfter(startDate);
      }
    }

    return isActiveCheck;
  }

  get licenseUserCount() {
    return this._dto.licenseUserCount;
  }

  getLicenseUpdateDTO(updateValue: Partial<LicenseUpdateDTO>): LicenseUpdateDTO {
    const updatedAdditionalCommitments =
      this._dto.additionalCommitments?.map(item => {
        return {
          committedUsage: item.committedUsage,
          discountRate: item.discountRate,
          type: item.type,
          usageMetricId: item.usageMetric ? item.usageMetric.usageMetricId : undefined,
          usageUnitPrice: item.usageUnitPrice,
          usageUnitPriceWhenExceed: item.usageUnitPriceWhenExceed,
        };
      }) ?? [];

    const updatedLicense: LicenseUpdateDTO = {
      additionalCommitments: updatedAdditionalCommitments,
      additionalProperties: this._dto.additionalProperties,
      currencyUnit: this._dto.currencyUnit,
      endDate: this._dto.endDate,
      licenseCode: this._dto.licenseCode,
      licenseId: this._dto.licenseId,
      licenseStatus: this._dto.licenseStatus,
      planId: this._dto.plan.planId,
      planOptionId: this._dto.planOption.planOptionId,
      startDate: this._dto.startDate,
      statusMessage: this._dto.statusMessage,
      timeZone: this._dto.timeZone,
      userLoginId: this._dto.userLoginId,
      renewalDate: this._dto.renewalDate,
      ...updateValue,
    };

    return updatedLicense;
  }
}
